'use client'

import Link from 'next/link'
import React from 'react'

import { StylePanelProps } from '@/lib/stylePanel'

import styles from './PartnerList.module.css'

export interface PartnerContentProps {
  name?: string
  offices?: Array<{
    name: string
    slug: string
    content?: {
      title?: string
    }
  }>
}

export interface PartnerProps {
  name: string
  content: PartnerContentProps
  full_slug?: string
}

export interface RegionProps {
  name: string
  partners: PartnerProps[]
  offices?: Array<{
    name: string
    advisers: Array<{
      name: string
    }>
  }>
}

export interface PartnerListProps extends StylePanelProps {
  title?: string
  year?: string
  regions: RegionProps[]
  variant?: 'primary' | 'secondary' | 'tertiary' | 'quaternary' | 'quinary'
  alignment?: 'center' | 'left' | 'right'
}

export const PhnxPartnerList: React.FC<PartnerListProps> = ({ title, year, regions = [] }) => {
  const selectedYear = year || '2025'

  const displayTitle = title || `Introducing our ${selectedYear} Partner Cohort`

  // Get current location from environment variable
  const location = process.env.NEXT_PUBLIC_NEXT_LOCATION || 'default'
  const isNZLocale = location === 'en-nz'

  // Clean office name by removing "Findex" prefix and "-old"/"- old" suffix
  const cleanOfficeName = (name?: string | null): string => {
    if (!name) return ''
    return name
      .replace(/^Findex\s+/i, '') // Remove "Findex " prefix
      .replace(/\s*-\s*old$/i, '') // Remove "- old" or "-old" suffix
      .trim()
  }

  // Generate URL for partner link based on location and partner region
  const getPartnerUrl = (partner: PartnerProps, regionName: string): string => {
    const isNewZealandPartner = regionName.toUpperCase().includes('NEW ZEALAND')

    // Strip 'en-nz/' from the beginning of the slug if present
    const cleanSlug = partner?.full_slug?.replace(/^en-nz\//, '') || ''

    if (isNewZealandPartner) {
      // For New Zealand partners, check if we're on NZ locale
      if (isNZLocale) {
        // If we're on NZ site and partner is NZ, use internal routing
        return cleanSlug
      } else {
        // If we're on AU site but partner is NZ, link to NZ site
        return `https://findex.co.nz/${cleanSlug}`
      }
    } else {
      // For AU partners
      if (isNZLocale) {
        // If we're on NZ site but partner is AU, link to AU site
        return `https://findex.com.au/${cleanSlug}`
      } else {
        // If we're on AU site and partner is AU, use internal routing
        return cleanSlug
      }
    }
  }

  // Group partners by their offices within each region
  const getOfficeGroups = (partners: PartnerProps[]) => {
    const officeGroups: Record<string, PartnerProps[]> = {}

    partners.forEach((partner) => {
      if (partner.content?.offices) {
        partner.content.offices.forEach((office) => {
          const rawOfficeName = office.content?.title || office.name
          const cleanedOfficeName = cleanOfficeName(rawOfficeName)

          if (!officeGroups[cleanedOfficeName]) {
            officeGroups[cleanedOfficeName] = []
          }

          // Check if partner already exists in this office (prevent duplicates)
          const partnerExists = officeGroups[cleanedOfficeName].some(
            (existingPartner) => existingPartner.name === partner.name
          )

          if (!partnerExists) {
            officeGroups[cleanedOfficeName].push(partner)
          }
        })
      }
    })

    return officeGroups
  }

  // Process regions to group ACT, WA, and Tasmania together and order based on locale
  const processRegions = () => {
    const processedRegions: Array<RegionProps | { name: string; combinedRegions: RegionProps[] }> =
      []
    let actRegion: RegionProps | undefined
    let waRegion: RegionProps | undefined
    let tasmaniaRegion: RegionProps | undefined
    let nzRegion: RegionProps | undefined
    const otherRegions: RegionProps[] = []

    regions.forEach((region) => {
      const regionName = region.name.toUpperCase()
      if (regionName.includes('AUSTRALIAN CAPITAL TERRITORY') || regionName === 'ACT') {
        actRegion = region
      } else if (
        regionName.includes('WESTERN AUSTRALIA') ||
        regionName === 'WA' ||
        regionName === 'WESTERN AUSTRALIA'
      ) {
        waRegion = region
      } else if (regionName.includes('TASMANIA') || regionName === 'TAS') {
        tasmaniaRegion = region
      } else if (regionName.includes('NEW ZEALAND') || regionName === 'NEW ZEALAND') {
        nzRegion = region
      } else {
        otherRegions.push(region)
      }
    })

    // Order regions based on locale
    if (isNZLocale) {
      // For NZ locale: New Zealand first, then others, then ACT & WA
      if (nzRegion) {
        processedRegions.push(nzRegion)
      }

      processedRegions.push(...otherRegions)

      // Combine ACT, WA, and Tasmania at the end for NZ locale
      if (actRegion || waRegion || tasmaniaRegion) {
        const combinedRegions = []
        if (actRegion) combinedRegions.push(actRegion)
        if (waRegion) combinedRegions.push(waRegion)
        if (tasmaniaRegion) combinedRegions.push(tasmaniaRegion)

        processedRegions.push({
          name: 'ACT, WA & TAS',
          combinedRegions,
        })
      }
    } else {
      // For AU locale: Others first, then ACT, WA & Tasmania, then New Zealand last
      processedRegions.push(...otherRegions)

      // Combine ACT, WA, and Tasmania in the middle for AU locale
      if (actRegion || waRegion || tasmaniaRegion) {
        const combinedRegions = []
        if (actRegion) combinedRegions.push(actRegion)
        if (waRegion) combinedRegions.push(waRegion)
        if (tasmaniaRegion) combinedRegions.push(tasmaniaRegion)

        processedRegions.push({
          name: 'ACT, WA & TAS',
          combinedRegions,
        })
      }

      // Add New Zealand at the end for AU locale
      if (nzRegion) {
        processedRegions.push(nzRegion)
      }
    }

    return processedRegions
  }

  const processedRegions = processRegions()

  return (
    <div className={`stylepanel ${styles['partner-list']}`}>
      <div className={`container ${styles['partner-list-container']}`}>
        <h2 className={styles['main-title']}>{displayTitle}</h2>

        <div className={styles['regions-grid']} data-region-count={processedRegions.length}>
          {processedRegions?.map((regionData, index) => {
            const isCombined = 'combinedRegions' in regionData

            if (isCombined) {
              // Render combined ACT & WA column with separate pills
              return (
                <div key={index} className={styles['region-column']}>
                  {regionData.combinedRegions.map((region, regionIndex) => (
                    <div key={regionIndex} className={styles['region-section']}>
                      <div
                        className={`${styles['region-pill']}`}
                        data-region={region.name.toLowerCase()}
                      >
                        {region.name.toUpperCase()}
                      </div>
                      <div className={styles['region-content']}>
                        {(() => {
                          const officeGroups = getOfficeGroups(region.partners || [])
                          return Object.entries(officeGroups).map(([officeName, partners]) => (
                            <div key={officeName} className={styles['office-group']}>
                              <h3 className={styles['office-name']}>{officeName}</h3>
                              <ul className={styles['advisers-list']}>
                                {partners.map((partner, partnerIndex) => {
                                  const partnerUrl = getPartnerUrl(partner, region.name)
                                  const isExternalLink = partnerUrl.startsWith('https://')
                                  return (
                                    <li key={partnerIndex} className={styles['adviser-item']}>
                                      {isExternalLink ? (
                                        <a
                                          href={partnerUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                        >
                                          <span>{partner.name}</span>
                                        </a>
                                      ) : (
                                        <Link href={partnerUrl} target="_blank">
                                          <span>{partner.name}</span>
                                        </Link>
                                      )}
                                    </li>
                                  )
                                })}
                              </ul>
                            </div>
                          ))
                        })()}
                      </div>
                    </div>
                  ))}
                </div>
              )
            } else {
              // Render regular single region column
              const region = regionData as RegionProps
              const officeGroups = getOfficeGroups(region.partners || [])

              return (
                <div key={index} className={styles['region-column']}>
                  <div className={styles['region-pill']} data-region={region.name.toLowerCase()}>
                    {region.name.toUpperCase()}
                  </div>
                  <div className={styles['region-content']}>
                    {Object.entries(officeGroups).map(([officeName, partners]) => (
                      <div key={officeName} className={styles['office-group']}>
                        <h3 className={styles['office-name']}>{officeName}</h3>
                        <ul className={styles['advisers-list']}>
                          {partners.map((partner, partnerIndex) => {
                            const partnerUrl = getPartnerUrl(partner, region.name)
                            const isExternalLink = partnerUrl.startsWith('https://')
                            return (
                              <li key={partnerIndex} className={styles['adviser-item']}>
                                {isExternalLink ? (
                                  <a href={partnerUrl} target="_blank" rel="noopener noreferrer">
                                    <span>{partner.name}</span>
                                  </a>
                                ) : (
                                  <Link href={partnerUrl} target="_blank">
                                    <span>{partner.name}</span>
                                  </Link>
                                )}
                              </li>
                            )
                          })}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )
            }
          })}
        </div>
      </div>
    </div>
  )
}
